/**
 * budgets.ts — Phase 192: Budget list CRUD module
 *
 * Create/edit/delete budgets via Bootstrap Modal + AJAX-POST signals.
 *
 * All state mutations POST to signal URLs from data-* attributes (never ?do= hardcoded).
 * Bootstrap Modal imported directly from 'bootstrap' package (feedback_bootstrap_not_on_window_use_import).
 * Idempotent init guard — safe to call multiple times (feedback_shared_component_every_page_init_double_bind).
 * fetch POST uses URLSearchParams + Content-Type: application/x-www-form-urlencoded.
 */

import { Modal } from 'bootstrap';  // import from package, not window global

export default {

    _inited: false,
    _deleteId: 0,
    _deleteUrl: '',
    _modal: null as Modal | null,
    _deleteModal: null as Modal | null,

    init(): void {
        // Idempotent init guard — prevent double-bind if init() is called twice
        if (this._inited) return;

        // Only activate on the /transactions/budgets page
        const budgetsList = document.getElementById('budgets-list');
        const createBtn   = document.querySelector<HTMLButtonElement>('.js-budget-create');
        const modalEl     = document.getElementById('budgetModal');
        const deleteModalEl = document.getElementById('budgetDeleteModal');

        if (!modalEl || !deleteModalEl) return;

        this._inited = true;

        this._modal       = Modal.getOrCreateInstance(modalEl);
        this._deleteModal = Modal.getOrCreateInstance(deleteModalEl);

        // ── Create button (both header + empty-state) ────────────────────────
        document.querySelectorAll<HTMLButtonElement>('.js-budget-create').forEach(btn => {
            btn.addEventListener('click', () => this._openCreateModal(btn));
        });

        // ── Edit + Delete buttons (delegated — table rows) ───────────────────
        if (budgetsList) {
            budgetsList.addEventListener('click', (e: Event) => {
                const editBtn = (e.target as HTMLElement).closest<HTMLButtonElement>('.js-budget-edit');
                if (editBtn) {
                    e.preventDefault();
                    this._openEditModal(editBtn);
                    return;
                }
                const delBtn = (e.target as HTMLElement).closest<HTMLButtonElement>('.js-budget-delete');
                if (delBtn) {
                    e.preventDefault();
                    this._openDeleteModal(delBtn);
                }
            });
        }

        // ── Modal save button ────────────────────────────────────────────────
        const saveBtn = document.getElementById('budget-modal-save');
        if (saveBtn) {
            saveBtn.addEventListener('click', () => this._saveBudget());
        }

        // ── Delete confirm button ────────────────────────────────────────────
        const deleteConfirmBtn = document.getElementById('budget-delete-confirm');
        if (deleteConfirmBtn) {
            deleteConfirmBtn.addEventListener('click', () => this._confirmDelete());
        }

        // ── Modal hidden → reset state ───────────────────────────────────────
        modalEl.addEventListener('hidden.bs.modal', () => this._resetModal());
    },

    // ── Create ───────────────────────────────────────────────────────────────

    _openCreateModal(btn: HTMLButtonElement): void {
        const labelEl   = document.getElementById('budgetModalLabel');
        const catPicker = document.getElementById('budget-modal-cat') as HTMLSelectElement | null;
        const catHint   = document.getElementById('budget-modal-cat-hint');

        if (labelEl)   labelEl.textContent = 'Add Budget';
        if (catPicker) catPicker.disabled = false;
        if (catHint)   catHint.textContent = '';

        // Store create URL on the modal save button so _saveBudget() can read it
        const saveBtn = document.getElementById('budget-modal-save');
        if (saveBtn) saveBtn.dataset.createUrl = btn.dataset.createUrl ?? '';

        this._modal?.show();
    },

    // ── Edit ─────────────────────────────────────────────────────────────────

    _openEditModal(btn: HTMLButtonElement): void {
        const labelEl     = document.getElementById('budgetModalLabel');
        const idInput     = document.getElementById('budget-modal-id') as HTMLInputElement | null;
        const catPicker   = document.getElementById('budget-modal-cat') as HTMLSelectElement | null;
        const catHint     = document.getElementById('budget-modal-cat-hint');
        const periodSel   = document.getElementById('budget-modal-period') as HTMLSelectElement | null;
        const amountInput = document.getElementById('budget-modal-amount') as HTMLInputElement | null;
        const threshInput = document.getElementById('budget-modal-threshold') as HTMLInputElement | null;
        const saveBtn     = document.getElementById('budget-modal-save');

        if (labelEl)   labelEl.textContent = 'Edit Budget';
        if (idInput)   idInput.value       = btn.dataset.budgetId ?? '';

        // Category is not editable on update — show the name, disable picker
        if (catPicker) {
            const subCatId = btn.dataset.subCatId ?? '';
            // Select the existing sub-cat (so the label shows correctly)
            for (let i = 0; i < catPicker.options.length; i++) {
                if (catPicker.options[i].value === subCatId) {
                    catPicker.selectedIndex = i;
                    break;
                }
            }
            catPicker.disabled = true;
        }
        if (catHint)   catHint.textContent = btn.dataset.name ?? '';
        if (periodSel) periodSel.value     = btn.dataset.period ?? 'monthly';
        if (amountInput) amountInput.value = btn.dataset.amount ?? '';
        if (threshInput) threshInput.value = btn.dataset.threshold ?? '80';

        if (saveBtn) saveBtn.dataset.editUrl = btn.dataset.editUrl ?? '';

        this._modal?.show();
    },

    // ── Delete ───────────────────────────────────────────────────────────────

    _openDeleteModal(btn: HTMLButtonElement): void {
        this._deleteId  = parseInt(btn.dataset.budgetId ?? '0', 10);
        this._deleteUrl = btn.dataset.deleteUrl ?? '';

        const nameEl = document.getElementById('budget-delete-name');
        if (nameEl) nameEl.textContent = btn.dataset.name ?? '';

        this._deleteModal?.show();
    },

    _confirmDelete(): void {
        if (!this._deleteId || !this._deleteUrl) return;

        const body = new URLSearchParams();
        body.set('id', String(this._deleteId));

        fetch(this._deleteUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success) {
                this._deleteModal?.hide();
                window.location.reload();
            } else {
                alert(data.message ?? 'Delete failed.');
            }
        })
        .catch(() => { alert('Delete failed — network error.'); });
    },

    // ── Save (create or edit) ─────────────────────────────────────────────────

    _saveBudget(): void {
        const idInput     = document.getElementById('budget-modal-id') as HTMLInputElement | null;
        const catPicker   = document.getElementById('budget-modal-cat') as HTMLSelectElement | null;
        const periodSel   = document.getElementById('budget-modal-period') as HTMLSelectElement | null;
        const amountInput = document.getElementById('budget-modal-amount') as HTMLInputElement | null;
        const threshInput = document.getElementById('budget-modal-threshold') as HTMLInputElement | null;
        const errorEl     = document.getElementById('budget-modal-error');
        const saveBtn     = document.getElementById('budget-modal-save');

        const showError = (msg: string) => {
            if (errorEl) {
                errorEl.textContent = msg;
                errorEl.classList.remove('d-none');
            }
        };

        if (errorEl) errorEl.classList.add('d-none');

        const budgetId    = parseInt(idInput?.value ?? '0', 10);
        const subCatId    = catPicker?.value ?? '';
        const period      = periodSel?.value ?? 'monthly';
        const amount      = parseFloat(amountInput?.value ?? '0');
        const threshold   = parseInt(threshInput?.value ?? '80', 10);

        // Client-side validation (defense-in-depth alongside server validation)
        if (!budgetId && !subCatId) {
            showError('Please select a category.');
            return;
        }
        if (isNaN(amount) || amount <= 0) {
            showError('Budget amount must be greater than 0.');
            return;
        }
        if (isNaN(threshold) || threshold < 1 || threshold > 100) {
            showError('Threshold must be between 1 and 100.');
            return;
        }

        const isEdit = budgetId > 0;
        const url    = isEdit
            ? (saveBtn?.dataset.editUrl ?? '')
            : (saveBtn?.dataset.createUrl ?? '');

        if (!url) {
            showError('Signal URL missing — please reload the page.');
            return;
        }

        const body = new URLSearchParams();
        if (isEdit) {
            body.set('id', String(budgetId));
        } else {
            body.set('sub_cat_id', subCatId);
        }
        body.set('period',        period);
        body.set('amount',        String(amount));
        body.set('threshold_pct', String(threshold));

        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success) {
                this._modal?.hide();
                window.location.reload();
            } else {
                showError(data.message ?? 'Save failed.');
            }
        })
        .catch(() => { showError('Save failed — network error.'); });
    },

    // ── Reset modal state ────────────────────────────────────────────────────

    _resetModal(): void {
        const idInput     = document.getElementById('budget-modal-id') as HTMLInputElement | null;
        const catPicker   = document.getElementById('budget-modal-cat') as HTMLSelectElement | null;
        const amountInput = document.getElementById('budget-modal-amount') as HTMLInputElement | null;
        const threshInput = document.getElementById('budget-modal-threshold') as HTMLInputElement | null;
        const errorEl     = document.getElementById('budget-modal-error');

        if (idInput)     idInput.value       = '';
        if (catPicker)   { catPicker.selectedIndex = 0; catPicker.disabled = false; }
        if (amountInput) amountInput.value   = '';
        if (threshInput) threshInput.value   = '80';
        if (errorEl)     errorEl.classList.add('d-none');
    },
};
